<template>
    <div class="panorama-controls abs b-1 r-1 depth-4">
        <div class="abs b-2.75 r-0 depth-4 f-col space-y-0.75">
            <button-group v-if="state !== VIEW_MODE_TYPES.DULA"
                          direction="vertical">
                <plan-button icon="plus"
                             tooltip="Увеличить"
                             tooltip-position="left"
                             class="stroke-white"
                             @click="$emit('zoom-in')" />
                <plan-button icon="minus"
                             tooltip="Уменьшить"
                             tooltip-position="left"
                             class="stroke-white"
                             @click="$emit('zoom-out')" />
            </button-group>
        </div>

        <!-- Minimap toggle -->
        <plan-button v-if="!withoutMinimap && state !== VIEW_MODE_TYPES.DULA"
                     v-show="plan"
                     :active="minimap"
                     tooltip="Миникарта"
                     icon="plan"
                     @click="toggleMinimap" />

        <div class="abs b-0 r-2.75 depth-4 f space-x-0.75">
            <!-- Floor switcher -->
            <el-dropdown v-if="!withoutFloorSwitcher"
                         placement="top-end"
                         trigger="click"
                         @command="goToPoint">
                <plan-button>
                    <span class="f-y-center text-white fs-0.8">
                        {{ currentFloorLabel }}<i class="el-icon-arrow-down _ml-0.25" />
                    </span>
                </plan-button>
                <el-dropdown-menu class="max-h-20 overflow-auto with-custom-scrollbar-dark">
                    <el-dropdown-item v-for="floor in floorOptions"
                                      :key="floor.id"
                                      :disabled="floor.disabled"
                                      :command="floor.point">
                        {{ floor.label }}
                    </el-dropdown-item>
                </el-dropdown-menu>
            </el-dropdown>

            <button-group v-if="state !== VIEW_MODE_TYPES.DULA">
                <plan-button :disabled="!hasPrevImage"
                             el-icon="el-icon-arrow-left text-white"
                             :class="{ 'is-disabled': !hasPrevImage }"
                             @click="$emit('on-prev')" />
                <plan-button :disabled="!hasNextImage"
                             el-icon="el-icon-arrow-right text-white"
                             :class="{ 'is-disabled': !hasNextImage }"
                             @click="$emit('on-next')" />
            </button-group>
        </div>

        <!-- Minimap -->
        <portal to="app">
            <draggable v-if="state !== VIEW_MODE_TYPES.DULA && plan"
                       v-show="minimap"
                       ref="draggable"
                       class="t-4.75 r-1 shadow rounded overflow-hidden"

                       resizeable
                       :size="minimapSize"
                       :min-width="minMinimapWidth"
                       :min-height="minMinimapHeight">
                <template #controller>
                    <div class="h-2 bg-el-dark f-y-center f-x-between">
                        <div class="f">
                            <plan-button icon="rect-outlined-small"
                                         tooltip="Области разметки плана"
                                         :active="visibleTechRoomList"
                                         squared
                                         @click="handleChangeVisibleTechRoomList" />
                            <plan-button icon="length"
                                         class="fill-white stroke-none"
                                         squared
                                         @click="resizeAsMinimap" />
                            <plan-button el-icon="el-icon-camera-solid text-white"
                                         tooltip="Камера 360"
                                         :active="filter.withCamera"
                                         squared
                                         @click="handleChangeMapFilter('withCamera')" />
                            <plan-button icon="rect-outlined-small"
                                         class="fill-white/50"
                                         tooltip="Разметка"
                                         :active="filter.withTechRooms"
                                         squared
                                         @click="handleChangeMapFilter('withTechRooms')" />
                        </div>

                        <div class="abs-x-center depth-100 t-0.5">
                            <icon name="drag-handle"
                                  class="el-icon-more wh-1 lh-2 fill-white" />
                        </div>
                        
                        <el-button type="text"
                                   class="button-squared text-white wh-2 p-0 f-center"
                                   icon="el-icon-close fs-1"
                                   @click="toggleMinimap" />
                    </div>
                </template>
    
                <floor-map ref="map"
                           class="border-left-1 border-right-1 border-bottom-1 border-el-dark rounded-bottom border-box overflow-hidden"
                           :plan-id="planId"
                           :plan-type="planType"
                           :filter="filter"
                           :controls="true"
                           :floor-image="planImage"
                           :points="points"
                           :tech-rooms="techRooms"
                           :show-bar-helpers="true"
                           :ui-hide="true"
                           :minimap-size="minimapSize"
                           :should-display-sight="state === VIEW_MODE_TYPES.PANORAMA"
                           @point:select="goToPoint"
                           @resize-as-minimap="resizeAsMinimap"
                           @tech-rooms-list-visible-change="changeTechRoomsListVisible"
                           @filter-change="changeFilter" />
            </draggable>
        </portal>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { VIEW_MODE_TYPES } from '@/utils/viewer/view-mode'

import { definePlanFilter } from '@/values/filters'

import FloorMap from '@/components/map/FloorMap'
import PlanButton from '@/components/map/PlanButton/PlanButton'
import ButtonGroup from '@/components/map/PlanButton/PlanButtonGroup'
import Draggable from '@/components/draggable/Draggable'

export default {
  name: 'PanoramaControls',
  
  components: {
    PlanButton,
    ButtonGroup,
    FloorMap,
    Draggable
  },

  props: {
    state: { type: String, default: null },

    plan: { type: Object, default: null },
    planImage: { type: Object, default: null },
    point: { type: Object, default: () => ({}) },
    currentFloor: { type: Object, default: () => ({}) },
    points: { type: Array, default: () => [] },
    techRooms: { type: Array, default: () => [] },

    hasPrevImage: { type: Boolean, default: false },
    hasNextImage: { type: Boolean, default: false },

    withoutMinimap: { type: Boolean, default: false },
    withoutFloorSwitcher: { type: Boolean, default: false }
  },

  data() {
    return {
      minimap: false,
      minimapSize: +get('minimap-size', 1, { storage: true }),

      minMinimapWidth: 300,
      minMinimapHeight: 220,

      visibleTechRoomList: false,

      filter: definePlanFilter({
        uiHide: true,
        planType: this.plan?.type
      }),

      VIEW_MODE_TYPES
    }
  },

  computed: {
    ...mapGetters('project', [ 'structure' ]),

    planId() {
      return this.plan?.id
    },

    planType() {
      return this.plan?.type
    },

    currentFloorLabel() {
      if (this.currentFloor.number) return 'Этаж ' + this.currentFloor.number
      return 'Неизвестный этаж'
    },

    floorOptions() {
      const houseId = this.point?.project_point_position?.house?.id
      const options = this.structure?.houses
        ?.find(h => h.id === houseId)?.floors
        ?.map(f => ({
          id: f.id,
          label: 'Этаж ' + f.number,
          disabled: f.id === this.currentFloor.id,
          point: f.floor_plans
            .find(p => p.defining_points.find(point => point.last_image_id))?.defining_points
            ?.find(point => point.last_image_id)
        }))
        ?.filter(f => f.point) || []

      return options
    }
  },

  methods: {
    toggleMinimap() {
      this.minimap = !this.minimap
      this.$nextTick(() => this.$refs.map && this.$refs.map.redraw())
    },

    goToPoint(point) {
      this.$emit('go-to-point', point)
    },

    resizeAsMinimap() {
      const k = 0.75
      const x = [+get('minimap-size', 1, { storage: true })].map(x => x >= (1 + k * 2) ? 1 : x + k)[0]

      this.$refs.draggable.changeSize(this.minMinimapWidth * x, this.minMinimapHeight * x)

      set('minimap-size', this.minimapSize = x, { storage: true })
    },

    changeTechRoomsListVisible() {
      this.visibleTechRoomList = !this.visibleTechRoomList
    },

    handleChangeVisibleTechRoomList() {
      this.$refs.map?.handleChangeVisibleTechRoomList()
    },

    handleChangeMapFilter(prop) {
      const { appliedFilter, confirmFilter } = this.$refs.map || {}
      const filter = { ...appliedFilter, [prop]: !appliedFilter[prop] } 
      
      this.changeFilter(filter)
      confirmFilter(filter)    
    },

    changeFilter(x) {
      this.filter = x
    }
  }
}
</script>

<style lang="scss">
.panorama-controls {
  .el-button {
    color: #FFFFFF;

    &:focus {
      color: #FFFFFF;
    }

    &:hover {
      color: #FFFFFF;
      background: #409EFF;
    }
  }
}
</style>